import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { IdentityUser } from '@model/identity';
import {
  FullLogoutUseCase,
  GetIdentityUserUseCase,
  GetLogoutUrlUseCase
} from '@usecase/identity';
import { OptionItem } from '@view/shared/molecules/option-item/option-item';
import { OptionListComponent } from '@view/shared/organisms/option-list/option-list.component';
import { TopBarComponent } from '@view/shared/organisms/top-bar/top-bar.component';
import { CommonModule } from '@angular/common';
import { IdentityOperations } from '@model/identity/operations';
import { RouteService } from '@helpers/routing/router.service';
import { AppTranslationService } from '@service/translation/app-translation.service';
import { AppSplashComponent } from '../splash/splash.component';
import { AppVersionService } from '../../../../infrastructure/services/app-version.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { catchError, finalize, of, switchMap } from 'rxjs';
import { PageErrorComponent } from '../page-error/page-error.component';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Componente que renderiza el layout de la aplicación
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-layout',
  imports: [
    RouterModule,
    CommonModule,
    TopBarComponent,
    OptionListComponent,
    AppSplashComponent,
    PageErrorComponent,
    ProgressSpinnerModule
  ],
  template: `
    <div class="flex flex-col h-screen">
      <section class="fixed top-0 left-0 right-0 z-20">
        @if (isUserMenuOpen()) {
          <div
            class="fixed inset-0 z-10"
            aria-hidden="true"
            (click)="onUserMenuStateChange('closed')"
          ></div>
        }
        <div class="relative z-20">
          <app-top-bar
            [username]="user().fullName"
            [lastAccess]="user().lastAccess"
            [userMenuOpen]="isUserMenuOpen()"
            (menuStateChange)="onMenuStateChange($event)"
            (userMenuStateChange)="onUserMenuStateChange($event)"
          ></app-top-bar>
          @if (isUserMenuOpen() && userMenu().length) {
            <div
              class="absolute right-8 top-16 z-30 w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg"
            >
              <app-option-list
                [items]="userMenu()"
                (itemClick)="onUserMenuItemClick($event)"
              ></app-option-list>
            </div>
          }
        </div>
      </section>
      <div class="flex flex-1 relative pt-16 pb-10 min-h-0">
        <!-- main -->
        <div class="flex-1 overflow-y-auto app-animate-all--ease min-h-0">
          @if (loading()) {
            <div
              class="card flex flex-1 justify-center items-center w-full h-full"
            >
              <p-progress-spinner ariaLabel="loading" />
            </div>
          }
          @if (!loading() && hasError()) {
            <app-page-error [httpErrorCode]="errorCode()"></app-page-error>
          }
          <router-outlet></router-outlet>
        </div>
      </div>
      <!-- Footer -->
      <footer
        class="fixed bottom-0 left-0 right-0 bg-blue-500 py-2 px-4 z-20"
        [ngClass]="{
          'md:pl-60': isMenuOpen(),
          'pl-0 md:pl-12': !isMenuOpen()
        }"
      >
        <div class="flex justify-between items-center text-xs text-white">
          <span>{{ copyrightText }}</span>
          <span>{{ appVersion }}</span>
        </div>
      </footer>
    </div>
    <app-splash class="fixed w-full top-0 bg-white z-10"></app-splash>
  `
})
export class LayoutComponent implements OnInit {
  /** caso de uso para obtener el usuario */
  private readonly usecase = inject(GetIdentityUserUseCase);
  private readonly getLogoutUrlUseCase = inject(GetLogoutUrlUseCase);
  private readonly fullLogoutUseCase = inject(FullLogoutUseCase);
  private readonly translator = inject(AppTranslationService);
  private readonly router = inject(RouteService);
  private readonly versionService = inject(AppVersionService);

  /** opciones de menú del usuario */
  userMenu = signal<OptionItem[]>([]);
  loading = signal(true);
  hasError = signal(false);
  errorCode = signal(503);
  logoutUrl = signal('/');

  user = signal<IdentityUser>({} as IdentityUser);
  userMenuState = signal('closed');
  menuItems = signal<OptionItem[]>([]);
  menuState = signal('closed');
  appVersion = '';
  copyrightText = '@Copyright SURA ' + new Date().getFullYear();

  /**
   * Inicialización del componente, se obtiene el usuario y se construye el menú
   */
  ngOnInit(): void {
    this.appVersion = this.versionService.getFullVersion();
    this.prepareUserMenu();

    this.usecase
      .execute()
      .pipe(
        switchMap((user) => {
          if (this.isEmptyUserResponse(user)) {
            this.resetUserMenu();
            return this.getLogoutUrlUseCase.execute();
          }

          this.user.set(user);
          this.prepareAppMenu(user);

          const logoutUrl = this.resolveLogoutUrlFromUser(user);

          if (logoutUrl) {
            return of(logoutUrl);
          }
          return this.getLogoutUrlUseCase.execute();
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status !== 401 && err.status !== 403) {
            this.errorCode.set(err.status);
            this.hasError.set(true);
            return of('');
          }
          this.resetUserMenu();
          return this.getLogoutUrlUseCase.execute();
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (logoutUrl: string) => {
          this.loading.set(false);
          this.logoutUrl.set(logoutUrl);
          this.prepareUserMenu();
        }
      });
  }

  /**
   * Se ejecuta cuando el estado del menú cambia
   * @param event puede ser opened o closed
   */
  onMenuStateChange(event: string) {
    this.menuState.set(event);
  }

  /**
   * Se ejecuta cuando el estado del menú de usuario cambia
   * @param event puede ser opened o closed
   */
  onUserMenuStateChange(event: string) {
    this.userMenuState.set(event);
  }

  /**
   * Se ejecuta cuando se hace click en un item del menú de usuario
   * @param item item del menú
   */
  onUserMenuItemClick(item: OptionItem) {
    if (item.id === 'logout') {
      this.userMenuState.set('closed');
      this.fullLogoutUseCase.execute(item.path);
    }
  }

  /**
   * Se ejecuta cuando se hace click en un item del menú sidebar
   * @param item item del menú
   */
  onMenuItemClicked(item: OptionItem) {
    this.router.navigateTo(item.path);
    this.menuState.set('closed');
  }

  /**
   * Determina si el menú de usuario está abierto
   */
  isUserMenuOpen = computed(() => this.userMenuState() === 'opened');

  isMenuOpen = computed(() => this.menuState() === 'opened');

  /**
   * Construye el menú de la aplicación
   * @param {IdentityUser} user usuario
   */
  private prepareAppMenu(user: IdentityUser) {
    const appMenu = IdentityOperations.getMenu(user);
    if (!appMenu.length || !appMenu[0].menuItems?.length) {
      this.menuItems.set([]);
      return;
    }

    this.menuItems.set(
      appMenu[0].menuItems.map((item) => ({
        id: item.cdresource.toString(),
        path: `/${item.contextPath}`,
        title: item.name,
        icon: item.image,
        active: false
      }))
    );
  }

  /**
   * Construye el menú de usuario
   */
  private prepareUserMenu() {
    this.userMenu.set([
      {
        id: 'logout',
        path: this.logoutUrl(),
        title: this.translator.get('security.logout'),
        icon: 'sign-out'
      }
    ]);
  }

  resetUserMenu(): void {
    this.user.set({} as IdentityUser);
    this.menuItems.set([]);
  }

  resolveLogoutUrlFromUser(user: IdentityUser): string {
    return user.fullUrlLogout || user.urlLogout;
  }

  isEmptyUserResponse(user: IdentityUser | null | undefined): boolean {
    if (!user) {
      return true;
    }

    return Object.keys(user).length === 0;
  }
}
